import { ThemeEvents } from "@theme/events";

const URL_PROPERTY_PARAM = "p";
const STYLE_ELEMENT_ID = "discount-gift-url-property-styles";

/** @type {Record<string, { value: string, key: string, percentage: number }>} */
const DISCOUNT_GIFT_PROPERTIES = {
  freedom_20: {
    value: "freedom_20",
    key: "_discount_gift",
    percentage: 20,
  },
};

function injectStyles() {
  if (document.getElementById(STYLE_ELEMENT_ID)) return;

  const style = document.createElement("style");
  style.id = STYLE_ELEMENT_ID;
  style.textContent = `
    .price-container.discount-gift-prices-active > .price,
    .price-container.discount-gift-prices-active > [role='group'],
    .price-container.discount-gift-prices-active > .custom-labels-list,
    .price-container.discount-gift-prices-active > .savings-block {
      display: none !important;
    }

    .discount-gift-prices {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
    }

    .discount-gift-prices__sale {
      font-size: inherit;
      color: inherit;
    }

    .discount-gift-prices__compare {
      font-size: smaller;
      opacity: 0.4;
      text-decoration: line-through;
    }

    .discount-gift-prices__badge {
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.03em;
      color: #fff;
      background: #f42717;
      padding: 3px 7px;
      border-radius: 2px;
      text-transform: uppercase;
      white-space: nowrap;
    }
  `;

  document.head.appendChild(style);
}

/**
 * @returns {string | null}
 */
export function getDiscountGiftValueFromUrl() {
  return new URLSearchParams(window.location.search).get(URL_PROPERTY_PARAM);
}

/**
 * @returns {{ value: string, key: string, percentage: number } | null}
 */
export function getDiscountGiftConfig() {
  const param = getDiscountGiftValueFromUrl();
  if (!param) return null;
  return DISCOUNT_GIFT_PROPERTIES[param] || null;
}

/**
 * @param {HTMLElement | null | undefined} productFormComponent
 * @returns {Record<string, string> | null}
 */
export function getDiscountGiftLineItemProperties(productFormComponent) {
  const config = getDiscountGiftConfig();
  if (!config) return null;

  if (productFormComponent instanceof HTMLElement) {
    const productUrl = productFormComponent.dataset.productUrl;
    const currentPath = window.location.pathname;
    const themePath =
      typeof Theme !== "undefined" && Theme.request?.path
        ? Theme.request.path
        : null;

    if (productUrl && productUrl !== currentPath && productUrl !== themePath) {
      return null;
    }
  }

  return { [config.key]: config.value };
}

/**
 * @param {number} cents
 * @returns {string}
 */
function formatMoneyCents(cents) {
  // @ts-ignore Shopify global on storefront
  if (
    typeof Shopify !== "undefined" &&
    typeof Shopify.formatMoney === "function"
  ) {
    // @ts-ignore Shopify global on storefront
    const format = window.Shopify?.money_format || "${{amount}}";
    // @ts-ignore Shopify global on storefront
    return Shopify.formatMoney(cents, format).replace(/\.00$/, "");
  }

  return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
    style: "currency",
    currency: "USD",
  })
    .format(cents / 100)
    .replace(/\.00$/, "");
}

/**
 * @returns {number | null}
 */
function getVariantPriceCents() {
  const scripts = document.querySelectorAll(
    'variant-picker script[type="application/json"]',
  );

  for (const script of scripts) {
    try {
      const variant = JSON.parse(script.textContent || "");
      if (variant?.price) return Number(variant.price);
    } catch {
      // ignore invalid JSON
    }
  }

  const priceContainer = getMainProductPriceContainer();
  const priceEl = priceContainer?.querySelector(".price");
  if (!priceEl?.textContent) return null;

  const numeric = Number(priceEl.textContent.replace(/[^0-9.]/g, ""));
  if (Number.isNaN(numeric)) return null;

  return Math.round(numeric * 100);
}

/**
 * @returns {HTMLElement | null}
 */
function getMainProductPriceContainer() {
  const productId =
    typeof Theme !== "undefined" && Theme.product?.id
      ? String(Theme.product.id)
      : null;
  if (!productId) return null;

  const productPrice = document.querySelector(
    `.product-details product-price[data-product-id="${productId}"]`,
  );
  if (!(productPrice instanceof HTMLElement)) return null;

  const container = productPrice.querySelector('[ref="priceContainer"]');
  return container instanceof HTMLElement ? container : null;
}

/**
 * Removes discount price UI from add-ons, recommendations, and sticky bar.
 */
function clearStaleDiscountPrices() {
  document
    .querySelectorAll(
      '.product-addon [ref="priceContainer"], .product-siblings [ref="priceContainer"], .sticky-add-to-cart__price [ref="priceContainer"]',
    )
    .forEach((container) => {
      if (!(container instanceof HTMLElement)) return;
      container.classList.remove("discount-gift-prices-active");
      container.querySelector(".discount-gift-prices")?.remove();
    });
}

/**
 * @returns {HTMLElement[]}
 */
function getPriceContainers() {
  const container = getMainProductPriceContainer();
  return container ? [container] : [];
}

/**
 * @param {HTMLElement} container
 * @param {number} comparePriceCents
 * @param {{ percentage: number }} config
 */
function updatePriceContainer(container, comparePriceCents, config) {
  const salePriceCents = Math.round(
    comparePriceCents * (1 - config.percentage / 100),
  );

  container.classList.add("discount-gift-prices-active");

  let block = container.querySelector(".discount-gift-prices");
  if (!(block instanceof HTMLElement)) {
    block = document.createElement("div");
    block.className = "discount-gift-prices";
    block.innerHTML = `
      <span class="discount-gift-prices__sale"></span>
      <span class="discount-gift-prices__compare"></span>
      <span class="discount-gift-prices__badge"></span>
    `;
    container.prepend(block);
  }

  const saleEl = block.querySelector(".discount-gift-prices__sale");
  const compareEl = block.querySelector(".discount-gift-prices__compare");
  const badgeEl = block.querySelector(".discount-gift-prices__badge");

  if (saleEl) saleEl.textContent = formatMoneyCents(salePriceCents);
  if (compareEl) compareEl.textContent = formatMoneyCents(comparePriceCents);
  if (badgeEl) badgeEl.textContent = `${config.percentage}% OFF`;
}

/**
 * @param {number} [priceCents]
 */
function applyDiscountPriceDisplay(priceCents) {
  const config = getDiscountGiftConfig();
  if (!config) return;

  clearStaleDiscountPrices();

  const comparePriceCents = priceCents ?? getVariantPriceCents();
  if (!comparePriceCents) return;

  getPriceContainers().forEach((container) => {
    updatePriceContainer(container, comparePriceCents, config);
  });
}

/**
 * @returns {HTMLFormElement[]}
 */
function getMainProductForms() {
  /** @type {HTMLFormElement[]} */
  const forms = [];

  document
    .querySelectorAll("product-form-component[data-product-url]")
    .forEach((component) => {
      if (!(component instanceof HTMLElement)) return;
      if (!getDiscountGiftLineItemProperties(component)) return;

      const form = component.querySelector("form.shopify-product-form");
      if (form instanceof HTMLFormElement) {
        forms.push(form);
      }
    });

  return forms;
}

/**
 * Auto-injects hidden property inputs inside the product form.
 */
export function applyDiscountGiftProperty() {
  const config = getDiscountGiftConfig();
  if (!config) return;

  getMainProductForms().forEach((form) => {
    const inputName = `properties[${config.key}]`;
    const existingInput = form.querySelector(`input[name="${inputName}"]`);

    const input =
      existingInput instanceof HTMLInputElement
        ? existingInput
        : (() => {
            const hiddenInput = document.createElement("input");
            hiddenInput.type = "hidden";
            hiddenInput.setAttribute("data-discount-gift-property-input", "");
            form.appendChild(hiddenInput);
            return hiddenInput;
          })();

    input.name = inputName;
    input.value = config.value;
  });
}

function init() {
  if (!getDiscountGiftConfig()) return;

  injectStyles();
  applyDiscountGiftProperty();
  applyDiscountPriceDisplay();

  document.addEventListener(ThemeEvents.variantUpdate, (event) => {
    const variantEvent =
      /** @type {import('@theme/events').VariantUpdateEvent} */ (event);
    const price = /** @type {{ price?: number } | null | undefined} */ (
      variantEvent.detail?.resource
    )?.price;
    requestAnimationFrame(() => {
      applyDiscountGiftProperty();
      applyDiscountPriceDisplay(price ? Number(price) : undefined);
    });
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
